import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterSignup = ({ variant = 'default' }) => {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) return;
    
    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, honeypot })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Subscription failed');
      }

      const data = await response.json();
      setStatus('success');
      setMessage(data.message || 'Successfully subscribed!');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Something went wrong. Please try again.');
    }
  };

  // Compact variant for footer
  if (variant === 'compact') {
    return (
      <div className="w-full" data-testid="newsletter-signup-compact">
        <h4 className="text-lg font-semibold text-white mb-3">Newsletter</h4>
        <p className="text-gray-400 text-sm mb-4">
          Get updates on new arrivals and special offers.
        </p>
        
        {status === 'success' ? (
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm">{message}</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Honeypot field - hidden from users */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />
            
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                required
                disabled={status === 'loading'}
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-500 px-4"
                disabled={status === 'loading'}
                data-testid="newsletter-submit-btn"
              >
                {status === 'loading' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            {status === 'error' && (
              <p className="text-red-400 text-xs flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {message}
              </p>
            )}
          </form>
        )}
      </div>
    );
  }

  // Default full variant for homepage section
  return (
    <section className="py-16 bg-gradient-to-r from-teal-700 to-teal-600" data-testid="newsletter-signup">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Mail className="w-12 h-12 text-teal-200 mx-auto mb-4" />
          <h2 className="text-3xl font-serif font-bold text-white mb-4">
            Stay Updated
          </h2>
          <p className="text-teal-100 mb-8">
            Subscribe to our newsletter to receive updates on new arrivals, special offers, and antique collecting tips.
          </p>

          {status === 'success' ? (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 max-w-md mx-auto">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-white font-medium">{message}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto">
              {/* Honeypot field */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-teal-200"
                  required
                  disabled={status === 'loading'}
                  data-testid="newsletter-email-input-full"
                />
                <Button 
                  type="submit" 
                  className="bg-white text-teal-700 hover:bg-teal-50 font-semibold px-8"
                  disabled={status === 'loading'}
                  data-testid="newsletter-submit-btn-full"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    'Subscribe'
                  )}
                </Button>
              </div>
              
              {status === 'error' && (
                <p className="text-red-300 text-sm mt-3 flex items-center justify-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {message}
                </p>
              )}
              
              <p className="text-teal-200 text-xs mt-4">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;
